import { io } from 'socket.io-client';
import { getActiveSubscription, getActiveUser } from '@/data/session/session-globals';
import { http } from '@pendo/http';

export const WS_EVENT_TYPES = {
    THREADS_BY_DOCUMENT_ID: 'threadsByDocumentId',
    NEW_THREAD: 'newThread',
    DELETED_THREAD: 'deletedThread',
    NEW_COMMENT: 'newComment',
    DELETED_COMMENT: 'deletedComment',
    EDITED_COMMENT: 'editedComment',
    NEW_EMOJI_REACTION: 'newEmojiReaction',
    DELETED_EMOJI_REACTION: 'deletedEmojiReaction',
};

const DOCUMENT_ID_DELIMITER = '_:';

const validCommentKinds = [
    'dashboard',
    'replay',
    'crosstabreplay',
    'savedclip',
    'idea',
    'feedback',
];

// Holds a map of documentIds and each callback registered to the documentId
const documentIdCallbacks = new Map();
let socket;

// connect to server
export function connectToWebSocketServer ({ dispatch }) {
    if (socket) return;

    const serverUrl = `wss://${window.location.host}/in-app-commenting`;
    const opts = {
        path: '/ws2/socket.io',
        transports: ['websocket'],
        forceNew: false,
        autoConnect: false,
    };
    socket = io(serverUrl, opts);
    socket.connect();

    socket.on('connect', () => {
        dispatch('pendoCollaboration/init', null, { root: true });

        // Rejoin rooms on reconnection
        if (documentIdCallbacks.size > 0) {
            const subId = getActiveSubscription()?.id;
            for (const documentId of documentIdCallbacks.keys()) {
                socket.emit('subscribeToDocument', { subId, documentId });
            }
        }
    });

    socket.on('connect_error', () => {
        dispatch('pendoCollaboration/teardown', null, { root: true });
    });

    socket.on('disconnect', (reason) => {
        dispatch('pendoCollaboration/teardown', null, { root: true });
        // socket.io won't auto-reconnect for these reasons
        if (reason === 'io server disconnect' || reason === 'io client disconnect') {
            socket = null;
            documentIdCallbacks.clear();
        }
        // For transport errors/timeouts, socket.io reconnects automatically
        // and the 'connect' handler will rejoin rooms
    });

    socket.on('message', ({ type, documentId, data }) => {
        documentIdCallbacks.get(documentId)?.forEach((cb) => cb({ type, data }));
    });
}

export function subscribeToDocument ({ documentId, callback }) {
    if (!socket) return () => {};

    // Join the room if not already subscribed
    if (!documentIdCallbacks.has(documentId)) {
        documentIdCallbacks.set(documentId, new Set());
        const subId = getActiveSubscription()?.id;
        socket.emit('subscribeToDocument', { subId, documentId });
    }

    if(callback) {
        documentIdCallbacks.get(documentId).add(callback);
    }

    // Return unsubscribe function
    return function unsubscribe () {
        const callbacks = documentIdCallbacks.get(documentId);
        if (!callbacks) return;
        callbacks.delete(callback);
        if (callbacks.size === 0) {
            documentIdCallbacks.delete(documentId);
            socket?.emit('leaveRooms', { documentId });
        }
    };
}

function buildPermissionEntitiesField ({ documentId, widgetId }) {
    let [ kind, ...idParts ] = documentId.split(DOCUMENT_ID_DELIMITER);
    // handles the case where the delimiter appears in the id
    const id = idParts.join(DOCUMENT_ID_DELIMITER);
    const entity = {};
    if (kind.toLowerCase() === 'crosstabreplay') {
        kind = 'replay';
    }
    if (kind.toLowerCase() === 'savedclip') {
        kind = 'saved clip';
    }
    entity[kind.toLowerCase()] = [id];
    if (widgetId) {
        entity.widget = [ widgetId ];
    }

    return entity;
}

export async function createThread ({ documentId, targetElementId, textBody, mentionedUserIds, contextJson, pageUrl, pageTitle }) {
    if (!documentId) {
        console.error('Missing required field documentId');

        return;
    }
    const subId = getActiveSubscription()?.id;
    const entities = buildPermissionEntitiesField({ documentId, widgetId: targetElementId });
    await synchronousSocketCall(socket, 'createThread', { subId, documentId, targetElementId, textBody, mentionedUserIds, entities, contextJson, pageUrl, pageTitle });
}

export async function deleteThread ({ documentId, threadId }) {
    const subId = getActiveSubscription()?.id;
    await synchronousSocketCall(socket, 'deleteThread', { subId, documentId, threadId });
}

export async function addComment ({ documentId, textBody, mentionedUserIds, threadId }) {
    const subId = getActiveSubscription()?.id;
    await synchronousSocketCall(socket, 'addComment', { subId, documentId, textBody, mentionedUserIds, threadId });
}

export async function deleteComment ({ documentId, threadId, commentId }) {
    const subId = getActiveSubscription()?.id;
    await synchronousSocketCall(socket, 'deleteComment', { subId, documentId, threadId, commentId });
}

export async function editComment ({ documentId, textBody, mentionedUserIds, threadId, commentId }) {
    const subId = getActiveSubscription()?.id;
    await synchronousSocketCall(socket, 'editComment', { subId, documentId, textBody, mentionedUserIds, threadId, commentId });
}

export async function addEmojiReaction ({ documentId, threadId, commentId, emojiCode }) {
    const subId = getActiveSubscription()?.id;
    await synchronousSocketCall(socket, 'addEmojiReaction', { subId, documentId, threadId, commentId, emojiCode });
}

export async function deleteEmojiReaction ({ documentId, threadId, commentId, emojiCode }) {
    const subId = getActiveSubscription()?.id;
    await synchronousSocketCall(socket, 'deleteEmojiReaction', { subId, documentId, threadId, commentId, emojiCode });
}

export async function getNotifications () {
    const subId = getActiveSubscription()?.id;
    await synchronousSocketCall(socket, 'getAllNotifications', { subId });
}

export async function synchronousSocketCall (socket, messageName, messageData = {}) {
    if(!socket) throw new Error('Socket was not provided or is not yet initialized');

    const response = await socket.timeout(10000).emitWithAck(messageName, messageData);

    if(response.isError) {
        throw response;
    }

    return response;
}

export function generateDocumentId ({ kind, id }) {
    if (!validCommentKinds.includes(kind)) {
        console.error(`Invalid kind ${kind} provided.`);
    }
    const prefix = kind.toUpperCase();

    return `${prefix}${DOCUMENT_ID_DELIMITER}${id}`;
}

export async function markThreadAsRead ({ threadId }) {
    const { data } = await http.put(`/api/s/_SID_/in-app-commenting/thread/${threadId}/read`);

    return data;
}

export function handleCommentingSocketMessages (commentThreads, { type, data }) {
    switch (type) {
        case WS_EVENT_TYPES.THREADS_BY_DOCUMENT_ID: {
            const next = new Map();
            if (data) {
                data.forEach((thread) => {
                    if (thread.inAppCommentThread?.id) {
                        next.set(thread.inAppCommentThread.id, thread);
                    }
                });
            }

            return next;
        }
        case WS_EVENT_TYPES.NEW_THREAD:
            if (!data?.inAppCommentThread?.id) break;

            return new Map(commentThreads).set(data.inAppCommentThread.id, data);
        case WS_EVENT_TYPES.DELETED_THREAD: {
            if (!data?.threadId) break;
            const next = new Map(commentThreads);
            next.delete(data.threadId);

            return next;
        }
        case WS_EVENT_TYPES.NEW_COMMENT: {
            const threadToUpdate = commentThreads.get(data.inAppCommentThreadId);
            if (!threadToUpdate) break;

            return new Map(commentThreads).set(data.inAppCommentThreadId, {
                ...threadToUpdate,
                comments: [
                    ...threadToUpdate.comments,
                    data,
                ].slice().sort((a, b) => a.createdAt - b.createdAt),
            });
        }
        case WS_EVENT_TYPES.EDITED_COMMENT: {
            const threadToUpdate = commentThreads.get(data.inAppCommentThreadId);
            if (!threadToUpdate) break;

            return new Map(commentThreads).set(data.inAppCommentThreadId, {
                ...threadToUpdate,
                comments: threadToUpdate.comments.map((comment) => {
                    if (comment.id === data.id) {
                        return {
                            ...comment,
                            textBody: data.textBody,
                            mentionedUserIds: data.mentionedUserIds || [],
                            lastUpdatedAt: data.lastUpdatedAt ?? comment.lastUpdatedAt,
                        };
                    }

                    return comment;
                }),
            });
        }
        case WS_EVENT_TYPES.DELETED_COMMENT: {
            const threadToUpdate = commentThreads.get(data.threadId);
            if (!threadToUpdate) break;

            return new Map(commentThreads).set(data.threadId, {
                ...threadToUpdate,
                comments: threadToUpdate.comments.map((comment) => {
                    if (comment.id === data.commentId) {
                        return {
                            ...comment,
                            deleted: true,
                            textBody: '',
                            mentionedUserIds: [],
                        };
                    }

                    return comment;
                }),
            });
        }
        case WS_EVENT_TYPES.NEW_EMOJI_REACTION:
        case WS_EVENT_TYPES.DELETED_EMOJI_REACTION: {
            const threadToUpdate = commentThreads.get(data.inAppCommentThreadId);
            if (!threadToUpdate) break;

            return new Map(commentThreads).set(data.inAppCommentThreadId, {
                ...threadToUpdate,
                comments: threadToUpdate.comments.map((comment) => {
                    if (comment.id === data.id) {
                        return {
                            ...comment,
                            reactions: data.reactions || [],
                        };
                    }

                    return comment;
                }),
            });
        }
        default:
            return commentThreads;
    }

    return commentThreads;
}

export function hasUnreadComments (comments, lastReadAt) {
    const currentUserId = getActiveUser()?.id;

    return comments.some((comment) => comment.createdAt > lastReadAt && comment.createdByUser?.id !== currentUserId);
}

export function buildLinkToThread (thread, route, toPathFn) {
    const { entities, id: threadId } = thread.inAppCommentThread;
    const { name, query } = route;
    let linkPath;
    if (entities?.dashboard) {
        linkPath = toPathFn({
            name,
            params: {
                id: entities.dashboard[0],
            },
            query: {
                widgetId: entities.widget?.[0],
                threadId,
            },
        });
    }
    if (entities?.feedback || entities?.idea) {
        linkPath = toPathFn({
            name,
            query: {
                openItem: query?.openItem,
                threadId,
            },
        });
    }

    if (!linkPath) return null;

    return `${window.location.origin}${linkPath}`;
}
